document.addEventListener("DOMContentLoaded", () => {
  const olfactiveMapping = document.querySelector(".olfactive-mapping");
  if (!olfactiveMapping) return;

  const olfactiveSquare = olfactiveMapping.querySelector(".olfactive-square");
  if (!olfactiveSquare) return;

  let overlayElement = null;
  let dynamicCloseButton = null;
  let clonedOlfactiveMap = null;
  let clonedMapResizeObserver = null;
  let triggerElement = null; // To store the element that opened the modal
  let keydownHandler = null; // To store the bound event handler for keydown

  const zoomInSvgOriginal = olfactiveSquare.innerHTML;
  const closeSvg = `
    <svg class="zoom-out-svg" width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="10.4575" y1="1.17923" x2="0.174292" y2="11.1792" stroke="#000000" stroke-width="0.5"/>
      <line x1="0.179646" y1="0.82614" x2="10.1549" y2="11.1334" stroke="#000000" stroke-width="0.5"/>
    </svg>
  `;

  const baseWidth = 400; // The original width for which pixel values were set

  const h3FontRatio = 12 / baseWidth;
  const axisSpanFontRatio = 9 / baseWidth;
  const textSpanFontRatio = 5 / baseWidth;
  const textSpanLineHeightRatio = 6 / baseWidth;
  const imageWidthRatio = 26 / baseWidth;

  // Parameterized updateFontSizes function
  function updateFontSizes(targetMapElement) {
    if (!targetMapElement) return;
    const containerWidth = targetMapElement.offsetWidth;
    const h3 = targetMapElement.querySelector("h2");
    const xSpans = targetMapElement.querySelectorAll(".x-axis span");
    const ySpans = targetMapElement.querySelectorAll(".y-axis span");
    const textSpans = targetMapElement.querySelectorAll(".olfactive-text span");

    if (h3) {
      h3.style.fontSize = containerWidth * h3FontRatio + "px";
    }
    xSpans.forEach((span) => {
      span.style.fontSize = containerWidth * axisSpanFontRatio + "px";
    });
    ySpans.forEach((span) => {
      span.style.fontSize = containerWidth * axisSpanFontRatio + "px";
    });
    textSpans.forEach((span) => {
      span.style.fontSize = containerWidth * textSpanFontRatio + "px";
      span.style.lineHeight = containerWidth * textSpanLineHeightRatio + "px";
    });

    const textImages = targetMapElement.querySelectorAll(
      ".olfactive-text span img"
    );
    textImages.forEach((img) => {
      img.style.width = containerWidth * imageWidthRatio + "px";
      img.style.height = "auto"; // or set height same as width for square images
    });
  }

  function trapFocusInModal(event) {
    if (event.key !== "Tab" || !clonedOlfactiveMap) {
      return;
    }

    const focusableElements = Array.from(
      clonedOlfactiveMap.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (el) =>
        el.offsetWidth > 0 &&
        el.offsetHeight > 0 &&
        !el.disabled &&
        el.offsetParent !== null
    );

    if (focusableElements.length === 0) {
      event.preventDefault(); // No focusable elements, prevent tabbing out
      return;
    }

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement =
      focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstFocusableElement) {
        lastFocusableElement.focus();
        event.preventDefault();
      }
    } else {
      // Tab
      if (document.activeElement === lastFocusableElement) {
        firstFocusableElement.focus();
        event.preventDefault();
      }
    }
    // If focus is somehow outside the modal, bring it to the first element.
    // This handles edge cases where focus might escape the trap.
    if (
      !clonedOlfactiveMap.contains(document.activeElement) &&
      focusableElements.length > 0
    ) {
      firstFocusableElement.focus();
    }
  }

  function performZoomOutActions() {
    if (document.removeEventListener && keydownHandler) {
      document.removeEventListener("keydown", keydownHandler);
      keydownHandler = null;
    }

    if (overlayElement) {
      overlayElement.remove();
      overlayElement = null;
    }
    if (dynamicCloseButton) {
      dynamicCloseButton.remove();
      dynamicCloseButton = null;
    }
    if (clonedMapResizeObserver) {
      clonedMapResizeObserver.disconnect();
      clonedMapResizeObserver = null;
    }
    if (clonedOlfactiveMap) {
      clonedOlfactiveMap.remove();
      clonedOlfactiveMap = null;
    }
    document.documentElement.classList.remove("scroll-locked-by-olfactive-map");

    if (triggerElement) {
      triggerElement.focus();
      triggerElement = null;
    }
  }

  function performZoomInActions() {
    if (clonedOlfactiveMap) return; // Already zoomed

    triggerElement = document.activeElement; // Store focus before opening modal

    document.documentElement.classList.add("scroll-locked-by-olfactive-map");

    clonedOlfactiveMap = olfactiveMapping.cloneNode(true);
    clonedOlfactiveMap.classList.add("olfactive-mapping--zoomed");

    // ARIA attributes for the modal
    clonedOlfactiveMap.setAttribute("role", "dialog");
    clonedOlfactiveMap.setAttribute("aria-modal", "true");

    const clonedH3 = clonedOlfactiveMap.querySelector("h2");
    if (clonedH3) {
      // Ensure the h3 has an ID for aria-labelledby.
      // Create a unique ID if one doesn't exist, or use a static one if appropriate.
      let titleId = clonedH3.id;
      if (!titleId) {
        titleId = "cloned-olfactive-map-title-" + Date.now(); // Simple unique ID
        clonedH3.id = titleId;
      }
      clonedOlfactiveMap.setAttribute("aria-labelledby", titleId);
    } else {
      // Fallback if no h3, though there should be one.
      clonedOlfactiveMap.setAttribute("aria-label", "Olfactive Map Details");
    }

    document.body.appendChild(clonedOlfactiveMap);
    updateFontSizes(clonedOlfactiveMap); // Initial font update for clone

    if (clonedMapResizeObserver) clonedMapResizeObserver.disconnect();
    clonedMapResizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === clonedOlfactiveMap) {
          updateFontSizes(clonedOlfactiveMap);
        }
      }
    });
    clonedMapResizeObserver.observe(clonedOlfactiveMap);

    if (!overlayElement) {
      overlayElement = document.createElement("div");
      overlayElement.className = "olfactive-map-overlay";
      overlayElement.addEventListener("click", performZoomOutActions);
      document.body.appendChild(overlayElement);
    }

    if (!dynamicCloseButton) {
      dynamicCloseButton = document.createElement("button");
      dynamicCloseButton.type = "button";
      dynamicCloseButton.className = "olfactive-map-dynamic-close-button";
      dynamicCloseButton.innerHTML = closeSvg;
      dynamicCloseButton.setAttribute(
        "aria-label",
        "Close olfactive map details"
      ); // ARIA label for close button
      dynamicCloseButton.addEventListener("click", performZoomOutActions);
      clonedOlfactiveMap.appendChild(dynamicCloseButton);
    }

    // Add focus trap
    if (!keydownHandler) {
      keydownHandler = trapFocusInModal; // Assign function directly
    }
    document.addEventListener("keydown", keydownHandler);

    // Set focus to the close button when modal opens
    if (dynamicCloseButton) {
      dynamicCloseButton.focus();
    }
  }

  // Initial setup for the original map
  updateFontSizes(olfactiveMapping);
  const mainMapResizeObserver = new ResizeObserver((entries) => {
    for (let entry of entries) {
      if (entry.target === olfactiveMapping && !clonedOlfactiveMap) {
        updateFontSizes(olfactiveMapping);
      }
    }
  });
  mainMapResizeObserver.observe(olfactiveMapping);

  olfactiveSquare.addEventListener("click", () => {
    if (!clonedOlfactiveMap) {
      performZoomInActions();
    }
  });
});
